// 操作引导 — shared context + progress helpers. The provider lives in ./TourProvider.js; anything
// that only needs to read the tour (TabIntro, the workspace, the sidebar) imports from here so no
// import cycle runs through ClientCrmModal.
import { createContext, useContext } from 'react';
import { ROUTES, SUPERVISOR_ROLES, TOUR_VERSION } from './tourSteps';

export const TourContext = createContext(null);

const noop = () => {};
const EMPTY_ROUTE = (route) => ({ route, total: (ROUTES[route] || []).length, last_step: 0, completed: false, dismissed: false, outdated: false, weekly_reminder: false });
export const emptyProgress = () => ({ coach: EMPTY_ROUTE('coach'), supervisor: EMPTY_ROUTE('supervisor') });

const FALLBACK = {
  available: false, active: false, demo: false, route: 'coach', stepIndex: -1, step: null, total: 0,
  progress: emptyProgress(), defaultRoute: 'coach', unfinished: false, introTick: 0, svTab: null, fixtures: null, viewN: 0,
  start: noop, next: noop, prev: noop, exit: noop, skip: noop, openInvite: noop, maybeInvite: noop,
  demoMemberClick: noop, openIntro: noop, resumeStep: () => 1,
};

/** Safe outside the provider (public pages, tests): every action is a no-op. */
export function useTour() {
  return useContext(TourContext) || FALLBACK;
}

export const rolesOfUser = (user) => {
  const out = new Set();
  const add = (r) => { const v = String(r || '').toLowerCase(); if (v) out.add(v); };
  if (user) {
    add(user.role); add(user.role_name);
    (Array.isArray(user.roles) ? user.roles : []).forEach((r) => add(typeof r === 'string' ? r : r && r.name));
  }
  return out;
};
export const defaultRouteFor = (user) => {
  const rs = rolesOfUser(user);
  return SUPERVISOR_ROLES.some((r) => rs.has(r)) ? 'supervisor' : 'coach';
};

/** A route still counts as "to do" (red dot) until finished on the current content. */
export const isUnfinished = (p) => !p || !p.completed || !!p.outdated;

/** Where 「继续」 picks up: the step last reached (1-based), or 1. */
export const resumeStepOf = (p, route) => {
  const total = (ROUTES[route] || []).length;
  if (!p || p.completed) return 1;
  return Math.min(total, Math.max(1, Number(p.last_step) || 1));
};

// ── local fallback (API not deployed / offline) ──
const LS_KEY = (uid) => `mm_tour_v${TOUR_VERSION}:${uid || 'anon'}`;
export function readLocal(uid) {
  try {
    const raw = window.localStorage.getItem(LS_KEY(uid));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    const base = emptyProgress();
    return { coach: { ...base.coach, ...(v.coach || {}) }, supervisor: { ...base.supervisor, ...(v.supervisor || {}) } };
  } catch (e) { return null; }
}
export function writeLocal(uid, progress) {
  try { window.localStorage.setItem(LS_KEY(uid), JSON.stringify(progress)); } catch (e) { /* private mode */ }
}

/** Server routes object → progress (fills totals / defaults). */
export function fromServer(routes) {
  const base = emptyProgress();
  const r = routes || {};
  return {
    coach: { ...base.coach, ...(r.coach || {}), total: base.coach.total },
    supervisor: { ...base.supervisor, ...(r.supervisor || {}), total: base.supervisor.total },
  };
}

/** Apply a PUT body to a progress object locally (same rules as the backend). */
export function applyPatch(progress, route, patch) {
  const cur = { ...(progress[route] || EMPTY_ROUTE(route)) };
  if (patch.last_step !== undefined) cur.last_step = patch.last_step;
  if (patch.completed !== undefined) {
    cur.completed = !!patch.completed;
    cur.outdated = false;
    if (patch.completed && patch.last_step === undefined) cur.last_step = cur.total;
  }
  if (patch.dismissed !== undefined) cur.dismissed = !!patch.dismissed;
  if (patch.weekly_reminder !== undefined) cur.weekly_reminder = !!patch.weekly_reminder;
  return { ...progress, [route]: cur };
}
